import { Slot } from "@gigpro/ui/Slot";
import { cn } from "@gigpro/ui/tailwind/cn";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-1 rounded-sm text-normal font-semibold transition-all duration-300 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand focus-visible:outline-dashed disabled:pointer-events-none disabled:text-gray [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        text: "text-brand hover:bg-background",
        raised: "bg-white text-brand shadow-down transition-shadow hover:shadow-transparent",
        outlined:
          "text-brand ring-2 ring-brand ring-inset hover:bg-background disabled:bg-background disabled:ring-transparent",
        contained: "bg-brand text-white hover:bg-brand-hover disabled:bg-background",
        destructive: "bg-error text-white hover:bg-error-hover",
        ghost: "hover:bg-light-gray/90",
        disabled: "bg-background text-gray",
      },
      size: {
        small: "px-2 py-1 [&_svg]:size-3",
        large: "px-4 py-2 [&_svg]:size-4",
        mainAction: "px-6 py-3 text-extraLarge [&_svg]:size-5",
      },
      icon: {
        false: "",
        true: "p-1",
      },
      loading: {
        true: "cursor-wait bg-background text-transparent before:absolute before:bottom-[calc(50%-12.5px)] before:loading-spinner before:w-5 before:text-medium-gray before:content-[''] disabled:text-transparent",
        false: "",
      },
    },
    compoundVariants: [
      {
        size: "small",
        loading: true,
        className: "before:bottom-[calc(50%-10px)] before:w-4",
      },
      {
        icon: true,
        loading: true,
        className: "bg-transparent before:bottom-[calc(50%-8px)] before:w-3.5",
      },
    ],
    defaultVariants: {
      variant: "text",
      size: "large",
      icon: false,
      loading: false,
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  ref?: React.Ref<HTMLButtonElement>;
  asChild?: boolean;
  "data-id"?: string;
  preload?: () => Promise<unknown>;
}

const Button = ({
  ref,
  className,
  variant,
  size,
  loading = false,
  icon,
  asChild = false,
  disabled = false,
  preload,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : "button";
  const warm = () => void preload?.().catch(() => {});
  const preloadProps: Pick<ButtonProps, "onPointerEnter" | "onFocus"> | undefined = preload && {
    onPointerEnter: (event) => {
      warm();
      props.onPointerEnter?.(event);
    },
    onFocus: (event) => {
      warm();
      props.onFocus?.(event);
    },
  };
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, icon, loading, className }))}
      ref={ref}
      disabled={disabled || !!loading}
      type="button"
      {...props}
      {...preloadProps}
    >
      {props.children}
    </Comp>
  );
};
Button.displayName = "Button";

export { Button, buttonVariants };
